import React, { useContext, useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import PDFCard from "../../../components/gcstudent/pdfcard/PDFCARD";
import "../../../assets/css/gcstudent/pdfcard/PDFCARD.css";
// ✅ Reusing existing classes instead of writing new CSS:
// - gcstudent-bb-detail-*   → ebook detail view (DigitalLibraryBooks.css)
// - gcstudent-bb-skeleton-* → skeleton loading (DigitalLibraryBooks.css)
// - gcStudent-featured-*    → section header/grid/view-all (FeaturedBooksSection.css)
import "../../../assets/css/gcstudent/digitallibrarybooks/DigitalLibraryBooks.css";
import "../../../assets/css/gcstudent/featuredbooksection/FeaturedBooksSection.css";

const LATEST_COUNT = 4;

/* ─────────────────────────────────────────────
   Ebook Detail — same markup/behaviour as the
   inline BookDetail in DigitalLibraryBooks.jsx,
   kept here so this section is fully standalone.
───────────────────────────────────────────── */
const EbookDetail = ({ book, onBack, isSaved, onToggleSave }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { baseUrlForFiles } = useContext(AppContext);

  const pdfUrl = book.pdf_file
    ? `${baseUrlForFiles}/ebooks/${book.pdf_file}`
    : null;

  const imgSrc =
    book.img && !imgFailed
      ? `${baseUrlForFiles}/${book.img}`
      : null;

  return (
    <div
      className="gcstudent-bb-detail-wrap gcstudent-bb-detail-anim"
      style={{ paddingTop: "100px", paddingLeft: "80px" }}
    >
      <button className="gcstudent-bb-back-btn" onClick={onBack}>
        ← Back to Latest PDFs
      </button>

      <Row className="gcstudent-bb-detail-row">
        <Col xs={12} md={4} lg={3} className="gcstudent-bb-detail-img-col">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={book.title}
              className="gcstudent-bb-detail-cover-img"
              onError={() => {
                console.error("Ebook cover image failed to load:", imgSrc, "raw book.img:", book.img);
                setImgFailed(true);
              }}
            />
          ) : (
            <div className="gcstudent-bb-detail-placeholder">
              <svg width="58" height="58" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.3">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                <path d="M14 2v6h6" />
              </svg>
              <span>PDF</span>
            </div>
          )}
        </Col>

        <Col xs={12} md={8} lg={9} className="gcstudent-bb-detail-info-col">
          <span className="gcstudent-bb-detail-cat-tag">{book.dept || "General"}</span>
          <h2 className="gcstudent-bb-detail-title">{book.title}</h2>

          <div className="gcstudent-bb-meta-row">
            <div className="gcstudent-bb-meta-pill">
              Author: <strong>{book.author}</strong>
            </div>
          </div>

          <div className="gcstudent-bb-action-row">
            {pdfUrl ? (
              <a href={pdfUrl} target="_blank" rel="noreferrer"
                className="gcstudent-bb-btn-pdf">
                Download PDF
              </a>
            ) : (
              <button className="gcstudent-bb-btn-pdf" disabled>
                No PDF Available
              </button>
            )}

            <button
              className={`gcstudent-dlb-save-btn ${isSaved ? "gcstudent-dlb-save-btn--saved" : ""}`}
              onClick={() => onToggleSave(book)}
            >
              {isSaved ? "✓ Saved" : "Save for Later"}
            </button>
          </div>

          {book.Summary && (
            <div className="gcstudent-bb-ai-card">
              <div className="gcstudent-bb-ai-header">🤖 AI Summary</div>
              <p className="gcstudent-bb-ai-text">{book.Summary}</p>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Featured PDFs Section — mirrors FeaturedBooksSection
───────────────────────────────────────────── */
const FeaturedPDFsSection = ({ onViewAll }) => {
  const [latestBooks,  setLatestBooks]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [savedBooks,   setSavedBooks]   = useState(new Map()); // Map<ebook_id, favourite_record_id>

  const { baseUrl, authToken, loggedInUser, userType } = useContext(AppContext);

  const isTeacher = userType === "teacher";
  const endpoints = isTeacher
    ? {
        getSaved: (id) => `${baseUrl}/teacher/ebooks/getSavedEbooks/${id}`,
        save: `${baseUrl}/teacher/ebooks/saveEbook`,
        remove: (favId) => `${baseUrl}/teacher/ebooks/removeSavedEbook/${favId}`,
        ownerField: "lbteacher_id",
      }
    : {
        getSaved: (id) => `${baseUrl}/student/notes/getSavedEbooks/${id}`,
        save: `${baseUrl}/student/ebooks/saveEbook`,
        remove: (favId) => `${baseUrl}/student/ebooks/removeSavedEbook/${favId}`,
        ownerField: "lbstudent_id",
      };

  /* Fetch latest ebooks */
  useEffect(() => {
    let mounted = true;

    axios
      .get(`${baseUrl}/general/ebooks/all`)
      .then((res) => {
        if (!mounted) return;
        if (res.data.success === true) {
          const books = (res.data.ebooks || []).filter(Boolean);

          const sorted = [...books].sort((a, b) => {
            if (a.created_at && b.created_at) {
              return new Date(b.created_at) - new Date(a.created_at);
            }
            return (b.id ?? 0) - (a.id ?? 0);
          });

          setLatestBooks(sorted.slice(0, LATEST_COUNT));
        }
      })
      .catch((err) => console.error("Latest PDFs fetch error:", err))
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [baseUrl]);

  /* Fetch already-saved ebooks, so "✓ Saved" persists */
  useEffect(() => {
    if (!loggedInUser?.id || !authToken) return;

    axios
      .get(endpoints.getSaved(loggedInUser.id), {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          const map = new Map();
          (res.data.ebooks || []).forEach((fav) => {
            if (fav.lbebook_id) map.set(fav.lbebook_id, fav.id);
          });
          setSavedBooks(map);
        }
      })
      .catch((err) => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, authToken, loggedInUser, userType]);

  const handleToggleSave = (book) => {
    if (!loggedInUser?.id) {
      Swal.fire({
        icon: "warning",
        title: "Login Required",
        text: "Please login to save ebooks to your Saved PDFs.",
        confirmButtonColor: "var(--primary-color)",
      });
      return;
    }

    const favouriteId = savedBooks.get(book.id);

    if (favouriteId) {
      axios
        .delete(endpoints.remove(favouriteId), {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((res) => {
          if (res.data.success === true) {
            setSavedBooks((prev) => {
              const next = new Map(prev);
              next.delete(book.id);
              return next;
            });
            Swal.fire({
              icon: "success",
              title: "Removed Successfully!",
              text: `"${book.title}" has been removed from your Saved PDFs.`,
              confirmButtonColor: "var(--primary-color)",
              timer: 1800,
              showConfirmButton: false,
            });
          }
        })
        .catch((err) => console.error(err));
    } else {
      axios
        .post(
          endpoints.save,
          {
            [endpoints.ownerField]: loggedInUser.id,
            lbebook_id: book.id,
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
        .then((res) => {
          if (res.data.success === true) {
            setSavedBooks((prev) => {
              const next = new Map(prev);
              next.set(book.id, res.data.saved.id);
              return next;
            });
            Swal.fire({
              icon: "success",
              title: "Saved Successfully!",
              text: "The ebook has been added to your Saved PDFs.",
              confirmButtonColor: "var(--primary-color)",
              timer: 1800,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Save Failed",
              text: res.data.message || "Could not save the ebook.",
              confirmButtonColor: "var(--primary-color)",
            });
          }
        })
        .catch((err) => console.error(err));
    }
  };

  // ── Click swaps in the ebook detail view, same pattern as FeaturedBooksSection ──
  if (selectedBook) {
    return (
      <EbookDetail
        book={selectedBook}
        onBack={() => setSelectedBook(null)}
        isSaved={savedBooks.has(selectedBook.id)}
        onToggleSave={handleToggleSave}
      />
    );
  }

  if (!loading && latestBooks.length === 0) return null;

  return (
    <div className="gcStudent-featured-section">
      <h2 className="gcStudent-featured-title">Latest PDFs</h2>
      <p className="gcStudent-featured-subtitle">
        Recently added digital books and e-resources
      </p>

      {loading ? (
        <Row
          className="gy-4 gx-3 gcStudent-featured-grid"
          style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "center" }}
        >
          {Array.from({ length: LATEST_COUNT }).map((_, i) => (
            <Col key={`skeleton-${i}`} xs={12} sm={6} md={4} lg={3}>
              <div className="gcstudent-bb-skeleton-card">
                <div className="gcstudent-bb-skeleton-img" />
                <div className="gcstudent-bb-skeleton-info">
                  <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-badge" />
                  <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-title" />
                  <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-title-short" />
                  <div className="gcstudent-bb-skeleton-line gcstudent-bb-skeleton-author" />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <Row
          className="gy-4 gx-3 gcStudent-featured-grid"
          style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", justifyContent: "center" }}
        >
          {latestBooks.map((book, i) => (
            <Col key={book.id} xs={12} sm={6} md={4} lg={3}>
              <PDFCard book={book} index={i} onClick={setSelectedBook} />
            </Col>
          ))}
        </Row>
      )}

      <div className="gcStudent-featured-viewall-row">
        <button className="gcStudent-featured-viewall-btn" onClick={onViewAll}>
          View All PDFs
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 12h14M13 5l7 7-7 7"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FeaturedPDFsSection;